<script>
import MarkdownIt from 'markdown-it'
import MembershipLevel from '@/model/dataLayer/api/MembershipSystem/MembershipLevel'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'

export default {
  name: 'MembershipSystemPreview',
  components: {
    PageSubHeader
  },
  props: {
    systemInfo: {
      type: Object,
      required: true,
      default: () => ({})
    },
    textColor: {
      type: String,
      default: 'black'
    }
  },
  data () {
    return {
      membershipLevels: [],
      selectedLevelIndex: 0,
      loading: false,
      levelSelectorDialog: false,
      lastConsumptionDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // Simulated last consumption date (7 days ago)
    }
  },
  computed: {
    displayInfo: function () {
      return this.systemInfo.displayInfo || {}
    },
    brandSecondaryColor: function () {
      return this.systemInfo.brandSecondaryColor || '#FFFFFF'
    },
    renderedMarkdown: function () {
      if (!this.systemInfo.descriptionTextMD) return ''
      const md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true
      })
      return md.render(this.systemInfo.descriptionTextMD)
    },
    selectedLevel: function () {
      return this.membershipLevels.length > 0 ? this.membershipLevels[this.selectedLevelIndex] : null
    },
    levelTextColor: function () {
      if (!this.selectedLevel || !this.selectedLevel.color) {
        return this.textColor
      }
      return this.calculateTextColor(this.selectedLevel.color)
    },
    membershipExpirationDate: function () {
      // Get points expiration days from systemInfo
      const expirationDays = this.systemInfo.pointRule && this.systemInfo.pointRule.expirationDays
        ? this.systemInfo.pointRule.expirationDays
        : 365 // Default to 365 days if not specified

      // Calculate expiration date based on last consumption date and expiration days
      const expirationDate = new Date(this.lastConsumptionDate)
      expirationDate.setDate(expirationDate.getDate() + expirationDays)

      // Format the date as YYYY-MM-DD
      return expirationDate.toISOString().split('T')[0]
    }
  },
  async mounted () {
    await this.loadMembershipLevels()
  },
  methods: {
    async loadMembershipLevels () {
      this.loading = true
      try {
        this.membershipLevels = await MembershipLevel.load()
        // Sort levels by levelOrder or requiredPoints
        this.membershipLevels.sort((a, b) => a.requiredPoints - b.requiredPoints)
      } catch (error) {
        console.error('Error loading membership levels:', error)
      } finally {
        this.loading = false
      }
    },
    selectLevel (index) {
      if (index >= 0 && index < this.membershipLevels.length) {
        this.selectedLevelIndex = index
        this.levelSelectorDialog = false // Close the dialog after selection
      }
    },
    // Calculate appropriate text color (black or white) based on background color
    calculateTextColor (bgColor) {
      // Default to black if no color is provided
      if (!bgColor) return 'black'

      // Calculate luminance
      let r, g, b

      // Remove # if present
      bgColor = bgColor.replace('#', '')

      // Handle shorthand hex (#RGB)
      if (bgColor.length === 3) {
        r = parseInt(bgColor[0] + bgColor[0], 16) / 255
        g = parseInt(bgColor[1] + bgColor[1], 16) / 255
        b = parseInt(bgColor[2] + bgColor[2], 16) / 255
      } else {
        r = parseInt(bgColor.substring(0, 2), 16) / 255
        g = parseInt(bgColor.substring(2, 4), 16) / 255
        b = parseInt(bgColor.substring(4, 6), 16) / 255
      }

      // Calculate luminance using WCAG formula
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

      // Use white text for dark backgrounds, black text for light backgrounds
      return luminance > 0.5 ? 'black' : 'white'
    }
  }
}
</script>

<template>
  <div class="preview-container">
    <!-- Mobile Phone Shell -->
    <div class="mobile-phone-shell">
      <div class="mobile-phone-header">
        <div class="mobile-phone-notch" />
        <div class="mobile-phone-status-bar">
          <div class="status-time">
            14:25
          </div>
          <div class="status-icons">
            <v-icon
              x-small
              class="mr-1"
              color="white"
            >
              mdi-signal-variant
            </v-icon>
            <v-icon
              x-small
              class="mr-1"
              color="white"
            >
              mdi-wifi
            </v-icon>
            <v-icon
              x-small
              color="white"
            >
              mdi-battery
            </v-icon>
          </div>
        </div>
      </div>
      <div class="mobile-phone-content">
        <!-- Loading indicator -->
        <v-overlay
          :value="loading"
          absolute
          :opacity="0.7"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </v-overlay>

        <!-- BLInfo Row -->
        <v-card
          v-if="systemInfo.horizontalLogoUrl || (displayInfo && displayInfo.name)"
          class="mb-4 pa-3"
          elevation="0"
          color="transparent"
        >
          <div class="d-flex align-center">
            <div class="flex-grow-0">
              <v-img
                v-if="systemInfo.horizontalLogoUrl"
                :src="systemInfo.horizontalLogoUrl"
                width="100"
                height="40"
                class="mr-3"
              />
            </div>

            <div
              v-if="!systemInfo.horizontalLogoUrl"
              class="d-flex flex-column"
            >
              <div class="text-subtitle-1 font-weight-medium">
                {{ displayInfo ? displayInfo.name : 'BL名称' }}
              </div>
              <div class="text-caption">
                {{ displayInfo ? displayInfo.description : 'BL描述信息' }}
              </div>
            </div>
          </div>
        </v-card>

        <v-card
          v-if="displayInfo"
          :color="selectedLevel && selectedLevel.color ? selectedLevel.color : displayInfo.color"
          :dark="levelTextColor === 'white'"
          :light="levelTextColor === 'black'"
          class="mb-4 mobile-card"
        >
          <div class="pa-4 d-flex align-end justify-center">
            <div
              class="d-flex flex-column"
              style="width: 100%"
            >
              <div class="text-h4">
                {{ displayInfo.name || '会员系统' }}
              </div>
              <div class="d-flex mb-3">
                <span class="text-caption ">个人档案</span>
                <v-icon
                  x-small
                  class="ml-1"
                >
                  mdi-arrow-right-circle
                </v-icon>
              </div>
              <div class="d-flex align-end justify-start mt-1">
                <span class="text-body-2">LV</span>
                <span
                  class="text-subtitle-2 font-weight-bold"
                  :style="{ color: brandSecondaryColor }"
                >&nbsp;{{
                  selectedLevel ? selectedLevel.levelOrder || 1 : 1
                }}</span>
                <span class="text-body-2"> /</span>
                <span class="text-subtitle-2 font-weight-bold">&nbsp;{{
                  selectedLevel ? selectedLevel.name : '白银会员'
                }}</span>
              </div>
              <div class="progress-linear mt-2">
                <div class="progress-container">
                  <div
                    class="progress-segment left-segment mr-2"
                    :style="{ width: '33.33%', backgroundColor: brandSecondaryColor }"
                  />
                  <div
                    class="progress-segment right-segment"
                    :style="{ width: '66.67%', backgroundColor: `${brandSecondaryColor}80` }"
                  />
                </div>
              </div>
              <div class="mt-2 text-body-2">
                33/100
              </div>
              <div class="mt-2 text-caption dimmed-text flex-column">
                <div>
                  会员资格有效期至
                </div>
                <div>
                  {{ membershipExpirationDate }}
                </div>
              </div>
            </div>
            <v-img
              class="ml-4"
              v-if="selectedLevel && selectedLevel.image ? true : displayInfo.imageUrl"
              :src="selectedLevel && selectedLevel.image ? selectedLevel.image : displayInfo.imageUrl"
              width="40%"
              contain
            />
          </div>
          <div class="bottomDisplay mobile-bottom-display">
            <div class="left-part">
              <v-row justify="end">
                <v-col cols="4">
                  <div class="d-flex flex-column align-center justify-center">
                    <div class="text-subtitle-2 font-weight-bold">
                      12
                    </div>
                    <div class="text-caption">
                      资产
                    </div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="d-flex flex-column align-center justify-center">
                    <div class="text-subtitle-2 font-weight-bold">
                      3
                    </div>
                    <div class="text-caption">
                      Awards
                    </div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="d-flex flex-column align-center justify-center">
                    <div class="text-subtitle-2 font-weight-bold">
                      8
                    </div>
                    <div class="text-caption">
                      消费
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
            <div class="separator text-subtitle-2">
              |
            </div>
            <div class="right-part d-flex flex-column align-center justify-center mt-1">
              <v-icon small>
                mdi-wallet-membership
              </v-icon>
              <div class="text-caption">
                我的权益
              </div>
            </div>
          </div>
        </v-card>
        <div
          v-if="systemInfo.promotionalImageUrl"
          class="mt-4"
        >
          <div class="promotional-image-container">
            <img
              :src="systemInfo.promotionalImageUrl"
              class="promotional-image"
              alt="Promotional Image"
            >
          </div>
        </div>
        <div
          v-if="systemInfo.descriptionTextMD"
          class="mt-4 pa-2 description-text-container"
        >
          <div
            v-html="renderedMarkdown"
            class="description-text"
          />
        </div>
        <div
          v-if="systemInfo.horizontalLogoUrl"
          class="mt-4 text-center"
        >
          <v-img
            :src="systemInfo.horizontalLogoUrl"
            max-height="60"
            contain
            class="mx-auto"
          />
        </div>
      </div>
      <div class="mobile-phone-footer">
        <div class="mobile-phone-home-button" />
      </div>
    </div>

    <!-- Button to open level selector dialog -->
    <div class="level-selector-button-container mt-4">
      <v-btn
        block
        color="primary"
        elevation="2"
        class="level-selector-button"
        @click="levelSelectorDialog = true"
      >
        <v-icon left>
          mdi-medal
        </v-icon>
        {{ selectedLevel ? `当前: ${selectedLevel.name}` : '选择预览等级' }}
      </v-btn>
    </div>

    <!-- Level Selector Dialog -->
    <v-dialog
      v-model="levelSelectorDialog"
      max-width="500"
    >
      <v-card>
        <page-sub-header class="mb-4 mx-4">
          <div class="d-flex align-center">
            <v-icon
              small
              class="mr-1"
            >
              mdi-medal
            </v-icon>
            预览会员等级
          </div>
          <template #subtitle>
            选择要在预览中显示的会员等级
          </template>
          <template #action>
            <v-btn
              icon
              @click="levelSelectorDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </page-sub-header>

        <v-card-text>
          <div class="level-selector">
            <div class="level-grid">
              <div
                v-for="(level, index) in membershipLevels"
                :key="level.id"
                class="level-item"
                :class="{ 'level-item-selected': selectedLevelIndex === index }"
                @click="selectLevel(index)"
              >
                <div
                  class="level-badge"
                  :style="{ backgroundColor: level.color || '#e0e0e0' }"
                >
                  <span>{{ level.levelOrder || index + 1 }}</span>
                </div>
                <div class="level-name">
                  {{ level.name }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.progress-linear {
  width: 100%;
  display: flex;
  justify-content: flex-start;
}

.progress-container {
  width: 80%;
  height: 12px;
  display: flex;
  border-radius: 15px;
  overflow: hidden;
}

.progress-segment {
  height: 100%;
}

.left-segment {
  width: 33.33%;
  border-radius: 15px;
}

.right-segment {
  width: 66.67%;
  border-radius: 15px;
}

.dimmed-text {
  opacity: 0.6;
}

.bottomDisplay {
  display: flex;
  align-items: center;
  height: 100px;
  background-color: rgba(255, 255, 255, 0.25);
}

.left-part {
  width: 75%;
}

.separator {
  margin: 0 8px;
}

.right-part {
  width: 25%;
}

.preview-container {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 16px;
}

/* Mobile Phone Shell Styles */
.mobile-phone-shell {
  width: 320px;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  border: 12px solid #333;
  border-radius: 36px;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.mobile-phone-header {
  height: 40px;
  background-color: #333;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.mobile-phone-notch {
  width: 120px;
  height: 24px;
  background-color: #333;
  border-radius: 0 0 12px 12px;
  position: absolute;
  top: 0;
}

.mobile-phone-status-bar {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
  color: #fff;
  font-size: 12px;
  position: relative;
  z-index: 1;
}

.status-time {
  font-weight: bold;
}

.status-icons {
  display: flex;
  align-items: center;
}

.mobile-phone-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  background-color: #f5f5f5;
  max-height: 600px;
}

.mobile-phone-footer {
  height: 40px;
  background-color: #333;
  display: flex;
  justify-content: center;
  align-items: center;
}

.mobile-phone-home-button {
  width: 40px;
  height: 4px;
  background-color: #999;
  border-radius: 2px;
}

/* Mobile card specific styles */
.mobile-card {
  margin: 0 !important;
  border-radius: 12px;
}

.mobile-card .v-image {
  max-height: 100px;
}

.mobile-bottom-display {
  height: 70px;
  font-size: 0.85rem;
}

/* Adjust progress bar for mobile */
.mobile-card .progress-container {
  width: 100%;
  height: 8px;
}

/* Make the preview container scrollable if content is too large */
.mobile-phone-content {
  scrollbar-width: thin;
}

.mobile-phone-content::-webkit-scrollbar {
  width: 4px;
}

.mobile-phone-content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

/* Promotional image styles */
.promotional-image-container {
  width: 100%;
  border-radius: 4px;
}

.promotional-image {
  width: 100%;
  height: auto;
  display: block;
}

/* Description text styles */
.description-text-container {
  width: 100%;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.description-text {
  font-size: 0.85rem;
  line-height: 1.5;
}

/* Level selector styles */
.level-selector-container {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.level-selector-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: #424242;
}

.level-selector {
  display: flex;
  justify-content: center;
}

.level-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 8px 4px;
  width: 100%;
}

/* For smaller screens, adjust to show more items per row */
@media (max-width: 400px) {
  .level-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
}

/* For larger screens, show more items per row if there are many levels */
@media (min-width: 600px) {
  .level-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

.level-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 12px 8px;
  border-radius: 8px;
  width: 100%;
}

.level-item:hover {
  background-color: #f5f5f5;
}

.level-item-selected {
  background-color: #e3f2fd;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #2196f3;
  position: relative;
}

.level-item-selected::after {
  content: "✓";
  position: absolute;
  top: 5px;
  right: 5px;
  color: #2196f3;
  font-weight: bold;
  font-size: 14px;
}

.level-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: white;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.level-name {
  font-size: 12px;
  text-align: center;
  font-weight: 500;
  color: #424242;
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Level selector button styles */
.level-selector-button-container {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.level-selector-button {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}
</style>
